import { useEffect, useState } from "react";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import Dropzone from "react-dropzone";
import { NumericFormat } from "react-number-format";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";
import SaveAltIcon from "@mui/icons-material/SaveAlt";

import { useAuth } from "src/hooks/useAuth";
import font from "src/styles/font";
import device from "src/styles/device";
import {
  checkNotEmpty,
  convertIntToCurrency,
  currencyToNumber,
} from "src/common";
import { Black, DarkGrey, GreyStroke, Primary } from "src/styles/color";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import CustomMap from "src/components/ui/Maps/CustomMaps";
import UploadImage from "src/components/upload/dropzone/UploadImage";

import { SpanLabel } from "src/modules/order/shipping-order/components";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: ${Black};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: #d8d8dc;
  margin: 24px 0;
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: ${({ slim }) => (slim ? "400" : "600")};
  line-height: 24px;
  margin: ${({ slim, unMargin }) => slim && !unMargin && "auto"};

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-adornedEnd {
    padding-right: 2px;
  }
`;

const StyledSelect = styled(Select)`
  border-radius: 16px;

  .MuiSelect-select {
    padding: 12px 16px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }

  @media ${device.mobileL} {
    .MuiSelect-select {
      padding: 8px 12px;
    }
  }
`;

const SumPrice = styled.h2`
  color: ${Primary};
  margin: 0 16px 0 0;
  font-weight: 600;
`;

const WrapperMapsContainer = styled.div`
  width: 100%;
  padding-top: calc(100% / (16 / 9));
  position: relative;
`;

const MapsContainer = styled.div`
  position: absolute;
  border: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 16px;
`;

const WrapperBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${device.mobileL} {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    .MuiFormControl-root {
      width: 100%;
      min-width: unset;
      max-width: unset;
      min-height: 40px;
    }
  }
`;

const StyledDatePicker = styled(DatePicker)`
  .MuiOutlinedInput-root {
    padding-right: 12px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }
`;

const WrapperUploadImage = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WrapperImgSlip = styled.div`
  width: 100%;
  max-width: 300px;
  height: auto;
`;

const ImgSlip = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ConfirmDialog = ({
  onClose,
  onSuccess,
  orderDetail,
  setIsLoading,
  isEdit = false,
}) => {
  const { user } = useAuth();

  const schemaConfirmed = yup.object().shape({
    method: yup.string().required("กรุณาเลือกรูปแบบการจัดส่ง"),
    fulfillmentProcess: yup.array().of(
      yup.object().shape({
        type: yup.string().required("กรุณาเลือกค่าจัดส่ง"),
        carrier: yup.string().required("กรุณาเลือกขนส่งที่ใช้ในการจัดส่ง"),
        carrierAdditional: yup.string().when("carrier", {
          is: (val) => val && val === "อื่นๆ",
          then: (schema) => schema.required("กรุณาระบุขนส่งที่ใช้จัดส่ง"),
          otherwise: (schema) => schema.notRequired(),
        }),
        shippingFee: yup.string().when("type", {
          is: (val) =>
            val && (val === "มีค่าจัดส่ง" || val === "เก็บเงินปลายทาง"),
          then: (schema) =>
            schema
              .required("กรุณาระบุค่าจัดส่ง")
              .test(
                "is-not-zero",
                "ค่าจัดส่งต้องไม่เท่ากับ 0",
                (value) => value !== 0 && value !== "0",
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
        pickupAddress: yup.object().shape({
          address: yup.string().when("method", {
            is: (val) => val && val === "ลูกค้ารับเอง",
            then: (schema) => schema.required("กรุณาระบุที่อยู่ร้านค้า"),
            otherwise: (schema) => schema.notRequired(),
          }),
          tel: yup.string().when("method", {
            is: (val) => val && val === "ลูกค้ารับเอง",
            then: (schema) => schema.required("กรุณาระบุเบอร์ติดต่อ"),
            otherwise: (schema) => schema.notRequired(),
          }),
          coordinates: yup.object().shape({
            lat: yup.string().when("method", {
              is: (val) => val && val === "ลูกค้ารับเอง",
              then: (schema) => schema.required("กรุณาระบุละจิจูด"),
              otherwise: (schema) => schema.notRequired(),
            }),
            long: yup.string().when("method", {
              is: (val) => val && val === "ลูกค้ารับเอง",
              then: (schema) => schema.required("กรุณาระบุลองจิจูด"),
              otherwise: (schema) => schema.notRequired(),
            }),
          }),
        }),
        remark: yup.string(),
      }),
    ),
    payment: yup
      .object()
      .shape({
        info: yup.object().shape({
          bankAccountName: yup.lazy((_, context) => {
            if (context.from !== undefined) {
              if (
                context.from[2].value.fulfillmentProcess[0].type ===
                "มีค่าจัดส่ง"
              )
                return yup.string().required("กรุณาระบุชื่อบัญชี");
              else return yup.string().notRequired();
            } else {
              return yup.string().notRequired();
            }
          }),
          bankNo: yup.lazy((_, context) => {
            if (context.from !== undefined) {
              if (
                context.from[2].value.fulfillmentProcess[0].type ===
                "มีค่าจัดส่ง"
              )
                return yup.string().required("กรุณาระบุเลขบัญชี");
              else return yup.string().notRequired();
            } else {
              return yup.string().notRequired();
            }
          }),
        }),
      })
      .when("fulfillmentProcess.0.type", {
        is: (val) => {
          return val && val === "มีค่าจัดส่ง";
        }, // ถ้า shippingAddress มีค่า
        then: (schema) => schema.required(), // บังคับให้ billingAddress เป็น required
        otherwise: (schema) => schema.notRequired(),
      }),
    documents: yup.object().shape({
      seller: yup.object().shape({
        qt: yup.object().shape({
          sellerQtFile: yup
            .mixed()
            .test("required", "กรุณาแนบไฟล์", (value) => value)
            .test(
              "fileSize",
              "ขนาดไฟล์ต้องไม่เกิน 4MB",
              (value) => value && value.size <= 4 * 1024 * 1024,
            ),
          sellerQtAdditionalFile: yup
            .mixed()
            .test("fileSize", "ขนาดไฟล์ต้องไม่เกิน 4MB", (value) => {
              if (!value) return true;
              return value && value.size <= 4 * 1024 * 1024;
            }),
        }),
      }),
    }),

    // method: yup.string().required("กรุณาเลือกรูปแบบการจัดส่ง"),
    // carrierFirst: yup.string().required("กรุณาเลือกขนส่งที่ใช้ในการจัดส่ง"),
    // carrierSecond: yup.string().required("กรุณาเลือกขนส่งที่ใช้ในการจัดส่ง"),
    // typeFirst: yup.string().required("กรุณาเลือกค่าจัดส่ง"),
    // typeSecond: yup.string().required("กรุณาเลือกค่าจัดส่ง"),
    // carrierAdditionalFirst: yup.string().when("carrierFirst", {
    //   is: (val) => val && val === "อื่นๆ",
    //   then: (schema) => schema.required("กรุณาระบุขนส่งที่ใช้จัดส่ง"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // carrierAdditionalSecond: yup.string().when("carrierSecond", {
    //   is: (val) => val && val === "อื่นๆ",
    //   then: (schema) => schema.required("กรุณาระบุขนส่งที่ใช้จัดส่ง"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // shippingFeeFirst: yup.string().when("typeFirst", {
    //   is: (val) => val && (val === "มีค่าจัดส่ง" || val === "เก็บเงินปลายทาง"),
    //   then: (schema) =>
    //     schema
    //       .required("กรุณาระบุค่าจัดส่ง")
    //       .test(
    //         "is-not-zero",
    //         "ค่าจัดส่งต้องไม่เท่ากับ 0",
    //         (value) => value !== 0 && value !== "0",
    //       ),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // shippingFeeSecond: yup.string().when("typeSecond", {
    //   is: (val) => val && (val === "มีค่าจัดส่ง" || val === "เก็บเงินปลายทาง"),
    //   then: (schema) =>
    //     schema
    //       .required("กรุณาระบุค่าจัดส่ง")
    //       .test(
    //         "is-not-zero",
    //         "ค่าจัดส่งต้องไม่เท่ากับ 0",
    //         (value) => value !== 0 && value !== "0",
    //       ),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // bankAccountName: yup.string().when("typeFirst", {
    //   is: (val) => val && val === "มีค่าจัดส่ง",
    //   then: (schema) => schema.required("กรุณาระบุชื่อบัญชี"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // bankNo: yup.string().when("typeFirst", {
    //   is: (val) => val && val === "มีค่าจัดส่ง",
    //   then: (schema) => schema.required("กรุณาระบุเลขที่บัญชี"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // qtFile: yup
    //   .mixed()
    //   .test("required", "กรุณาแนบไฟล์", (value) => value)
    //   .test(
    //     "fileSize",
    //     "ขนาดไฟล์ต้องไม่เกิน 4MB",
    //     (value) => value && value.size <= 4 * 1024 * 1024,
    //   ),
    // additionalFile: yup
    //   .mixed()
    //   .test("fileSize", "ขนาดไฟล์ต้องไม่เกิน 4MB", (value) => {
    //     if (!value) return true;
    //     return value && value.size <= 4 * 1024 * 1024;
    //   }),
    // remark: yup.string(),
    // address: yup.string().when("method", {
    //   is: (val) => val && val === "ลูกค้ารับเอง",
    //   then: (schema) => schema.required("กรุณาระบุที่อยู่ร้านค้า"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // tel: yup.string().when("method", {
    //   is: (val) => val && val === "ลูกค้ารับเอง",
    //   then: (schema) => schema.required("กรุณาระบุเบอร์ติดต่อ"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
  });

  const schemaFailed = yup.object().shape({
    failureReason: yup.string().required("กรุณาระบุสาเหตุที่ไม่พร้อมส่ง"),
  });

  const isAdmin = user?.role_id === 1;

  const initialDetail = {
    order: {},
    method: "ส่งไปถึงลูกค้าโดยตรง",
    fulfillmentProcess: [
      {
        type: "มีค่าจัดส่ง",
        carrier: "จัดส่งโดยร้านค้า",
        trackingNumber: null,
        trackingUrl: null,
        shippingFee: null,
        pickupAddress: {
          name: null,
          tel: null,
          address: null,
          coordinates: {
            lat: 13.6940782,
            lng: 100.6022862,
          },
        },
        shippingProofs: [],
        deliveryProofs: [],
        shippedDate: null,
        estimatedDeliveryDate: null,
        actualDeliveryDate: null,
        remark: null,
      },
    ],
    payment: {
      method: null,
      info: {
        bankAccountName: null,
        bankNo: null,
      },
      total: null,
      paymentDate: null,
      paymentProof: null,
    },
    status: {
      failureReason: null,
      value: "pending",
    },
    note: null,
    documents: {
      customer: {
        qt: {
          qtDate: null,
          qtNo: null,
          qtFile: null,
          additionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          invFile: null,
          additionalFile: null,
        },
      },
      xsurface: {
        po: {
          poDate: null,
          poNo: null,
          poFile: null,
          additionalFile: null,
        },
        vc: {
          vcDate: null,
          vcFile: null,
          additionalFile: null,
        },
      },
      seller: {
        qt: {
          qtDate: null,
          qtNo: null,
          qtFile: null,
          additionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          invFile: null,
          additionalFile: null,
        },
      },
    },
  };

  const [shippingDetail, setShippingDetail] = useState(initialDetail);
  const [isProductIsReady, setIsProductIsReady] = useState("ready");
  const validationSchema =
    isProductIsReady === "ready" ? schemaConfirmed : schemaFailed;

  const confirmedForm = {
    fulfillmentProcess: [
      {
        type: "มีค่าจัดส่ง",
        carrier: "จัดส่งโดยร้านค้า",
        carrierAdditional: "",
        shippingFee: "",
        pickupAddress: {
          address: "",
          tel: "",
          coordinates: {
            lat: 0,
            long: 0,
          },
        },
        remark: "",
      },
    ],
    payment: {
      info: {
        bankAccountName: "",
        bankNo: "",
      },
    },
    documents: {
      seller: {
        qt: {
          sellerQtFile: "",
          sellerQtAdditionalFile: "",
        },
      },
    },
    method: "ส่งไปถึงลูกค้าโดยตรง",
  };

  const failedUseForm = {
    failureReason: null,
  };
  const validationUseForm =
    isProductIsReady === "ready" ? confirmedForm : failedUseForm;

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: validationUseForm,
    resolver: yupResolver(validationSchema),
  });

  const isJSONString = (str) => {
    if (typeof str !== "string") return str;
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  const watchFields = watch();

  useEffect(() => {
    if (!!orderDetail) {
      setShippingDetail((order) => ({
        ...order,
        ...orderDetail,
        method: orderDetail.method
          ? isJSONString(orderDetail.method)
          : "ส่งไปถึงลูกค้าโดยตรง",
        fulfillmentProcess:
          orderDetail.fulfillmentProcess.length > 0
            ? orderDetail.fulfillmentProcess
            : initialDetail.fulfillmentProcess,
        payment: orderDetail.payment,
        documents: orderDetail.documents,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  const deliveryMethods = [
    "ส่งไปถึงลูกค้าโดยตรง",
    "ส่งไปที่ขนส่ง",
    "ลูกค้ารับเอง",
  ];

  const carriers = ["จัดส่งโดยร้านค้า", "Lalamove", "อื่นๆ"];

  const deliveryFee = ["มีค่าจัดส่ง", "ไม่มีค่าจัดส่ง", "เก็บเงินปลายทาง"];
  const multipleDeliveryFee = ["มีค่าจัดส่ง", "ไม่มีค่าจัดส่ง"];

  const checkErrors = () => {
    if (Object.keys(errors).length > 0) {
    }
  };

  const onSubmit = async (result) => {
    // try {
    //   setIsLoading(true);
    //   const payload = {
    //     ...shippingDetail,
    //     status: {
    //       value:
    //         isProductIsReady === "ready"
    //           ? isEdit
    //             ? shippingDetail?.status?.value
    //             : "confirmed"
    //           : "failed",
    //       failureReason:
    //         isProductIsReady === "ready"
    //           ? null
    //           : shippingDetail.status.failureReason,
    //     },
    //     order: orderDetail?.order?._id,
    //   };
    //   delete payload.orderDetail;
    //   const data = new FormData();
    //   for (const field in payload) {
    //     if (
    //       field === "order" ||
    //       field === "_id" ||
    //       field === "method" ||
    //       field === "note" ||
    //       field === "seller"
    //     ) {
    //       data.append(field, payload[field]);
    //     } else {
    //       data.append(field, JSON.stringify(payload[field]));
    //     }
    //   }
    //   let document = isJSONString(data.get("documents"));
    //   delete document.seller.qt.qtFile;
    //   delete document.seller.qt.additionalFile;
    //   data.set("documents", JSON.stringify(document));
    //   if (
    //     typeof payload.documents.seller.qt.qtFile === "object" &&
    //     payload.documents.seller.qt.qtFile !== null
    //   ) {
    //     if (payload.documents.seller.qt.qtFile.path) {
    //       data.append(
    //         "documents.seller.qt.qtFile",
    //         payload.documents.seller.qt.qtFile,
    //       );
    //     } else {
    //       document.seller.qt.qtFile = payload.documents.seller.qt.qtFile._id;
    //       data.set("documents", JSON.stringify(document));
    //     }
    //   }
    //   if (
    //     typeof payload.documents.seller.qt.additionalFile === "object" &&
    //     payload.documents.seller.qt.additionalFile !== null
    //   ) {
    //     if (payload.documents.seller.qt.additionalFile.path) {
    //       data.append(
    //         "documents.seller.qt.additionalFile",
    //         payload.documents.seller.qt.additionalFile,
    //       );
    //     } else {
    //       document.seller.qt.additionalFile =
    //         payload.documents.seller.qt.additionalFile._id;
    //       data.set("documents", JSON.stringify(document));
    //     }
    //   }
    //   await updateShippingOrder(shippingDetail._id, data);
    //   await onSuccess();
    //   enqueueSnackbar(`อัพเดทรายละเอียดการจัดส่งสินค้า สำเร็จ`, {
    //     variant: "success",
    //   });
    // } catch (err) {
    //   setIsLoading(false);
    //   console.log("errerr", err);
    //   enqueueSnackbar(err.response.data.message, {
    //     variant: "error",
    //   });
    // }
  };

  const ShippingFeeApplies = (index = 0) => {
    return (
      <>
        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>
            {`ค่าจัดส่ง (บาท)`} <span>*</span>
          </SpanLabel>
          <FormControl fullWidth>
            <Controller
              name="fulfillmentProcess[0].shippingFee"
              control={control}
              render={({ field }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  variant="outlined"
                  thousandSeparator=","
                  decimalSeparator="."
                  allowNegative={false}
                  decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
                  sx={{
                    width: "100%",
                    "& input": {
                      textAlign: "right",
                    },
                  }}
                  error={!!errors.fulfillmentProcess?.[0]?.shippingFee}
                  helperText={
                    errors.fulfillmentProcess?.[0]?.shippingFee?.message
                  }
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>หมายเหตุ</SpanLabel>
          <FormControl fullWidth>
            <Controller
              name="fulfillmentProcess[0].remark"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid container xs={12} mt={3} mb={0} justifyContent={"space-between"}>
          <Grid item display="flex" alignItems="center">
            <SpanTitle>ค่าจัดส่งรวมทั้งหมด</SpanTitle>
          </Grid>
          <Grid item display="flex" justifyContent="center">
            <SumPrice>
              {watchFields?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง"
                ? watchFields?.fulfillmentProcess.length > 1
                  ? convertIntToCurrency(
                      currencyToNumber(
                        watchFields?.fulfillmentProcess?.[0]?.shippingFee,
                      ) +
                        currencyToNumber(
                          watchFields?.fulfillmentProcess?.[1]?.shippingFee,
                        ),
                    )
                  : convertIntToCurrency(
                      watchFields?.fulfillmentProcess?.[0]?.shippingFee,
                    )
                : convertIntToCurrency(
                    watchFields?.fulfillmentProcess?.[1]?.shippingFee ?? 0,
                  )}
            </SumPrice>
            <SpanTitle slim>บาท</SpanTitle>
          </Grid>
        </Grid>

        <Line />

        <Grid container xs={12} justifyContent="space-between">
          <Grid container xs={12} mb={2}>
            <SpanTitle>ช่องทางการชำระเงิน</SpanTitle>
          </Grid>
          <Grid item xs={5.8}>
            <SpanLabel isMargin>
              ชื่อบัญชี <span>*</span>
            </SpanLabel>
            <FormControl fullWidth>
              <Controller
                name="payment.info.bankAccountName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.payment?.info?.bankAccountName}
                    helperText={errors.payment?.info?.bankAccountName?.message}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={5.8}>
            <SpanLabel isMargin>
              เลขบัญชี <span>*</span>
            </SpanLabel>
            <FormControl fullWidth>
              <Controller
                name="payment.info.bankNo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors?.payment?.info?.bankNo}
                    helperText={errors?.payment?.info?.bankNo?.message}
                  />
                )}
              />
            </FormControl>
          </Grid>

          {!isEdit && (
            <>
              <Line />

              <Grid container xs={12} mb={2}>
                <SpanTitle>เอกสาร</SpanTitle>
              </Grid>

              <Grid container xs={12}>
                <Grid item xs={12}>
                  <SpanLabel isMargin>
                    {`ใบเสนอราคา (ของร้านค้า)`} <span>*</span>
                  </SpanLabel>
                  <FormControl fullWidth>
                    <StyledTextField
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {checkNotEmpty(
                              watchFields?.documents?.seller?.qt?.sellerQtFile,
                            ) ? (
                              <IconButton
                                onClick={() =>
                                  setValue(
                                    "documents.seller.qt.sellerQtFile",
                                    "",
                                  )
                                }
                              >
                                <DeleteOutlineIcon
                                  sx={{
                                    color: Primary,
                                    cursor: "pointer",
                                  }}
                                />
                              </IconButton>
                            ) : (
                              <Controller
                                name="documents.seller.qt.sellerQtFile"
                                control={control}
                                render={({ field: { onChange } }) => (
                                  <Dropzone
                                    onDrop={(acceptedFiles) =>
                                      onChange(acceptedFiles[0])
                                    }
                                    accept={[".pdf"]}
                                  >
                                    {({ getRootProps, getInputProps }) => (
                                      <section>
                                        <div {...getRootProps()}>
                                          <input {...getInputProps()} />
                                          <div>
                                            <IconButton>
                                              <AttachFileIcon
                                                sx={{
                                                  color: Black,
                                                  cursor: "pointer",
                                                  fontSize: font.size.lg,
                                                  ":hover": {
                                                    color: "#e04132",
                                                  },
                                                }}
                                              />
                                            </IconButton>
                                          </div>
                                        </div>
                                      </section>
                                    )}
                                  </Dropzone>
                                )}
                              />
                            )}
                          </>
                        ),
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={
                        typeof watchFields?.documents?.seller?.qt
                          ?.sellerQtFile !== "object"
                          ? watchFields?.documents?.seller?.qt?.sellerQtFile
                          : watchFields?.documents?.seller?.qt?.sellerQtFile
                              ?.name
                      }
                    />
                    <FormHelperText
                      error={!!errors.documents?.seller?.qt?.sellerQtFile}
                    >
                      {errors.documents?.seller?.qt?.sellerQtFile?.message}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container xs={12} mt={2}>
                <Grid item xs={12}>
                  <SpanLabel isMargin>{`เอกสารเพิ่มเติม`}</SpanLabel>
                  <FormControl fullWidth>
                    <StyledTextField
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {checkNotEmpty(
                              watchFields?.documents?.seller?.qt
                                ?.sellerQtAdditionalFile,
                            ) ? (
                              <IconButton
                                onClick={() =>
                                  setValue(
                                    "documents.seller.qt.sellerQtAdditionalFile",
                                    "",
                                  )
                                }
                              >
                                <DeleteOutlineIcon
                                  sx={{
                                    color: Primary,
                                    cursor: "pointer",
                                  }}
                                />
                              </IconButton>
                            ) : (
                              <Controller
                                name="documents.seller.qt.sellerQtAdditionalFile"
                                control={control}
                                render={({ field: { onChange } }) => (
                                  <Dropzone
                                    onDrop={(acceptedFiles) =>
                                      onChange(acceptedFiles[0])
                                    }
                                    accept={[".pdf"]}
                                  >
                                    {({ getRootProps, getInputProps }) => (
                                      <section>
                                        <div {...getRootProps()}>
                                          <input {...getInputProps()} />
                                          <div>
                                            <IconButton>
                                              <AttachFileIcon
                                                sx={{
                                                  color: Black,
                                                  cursor: "pointer",
                                                  fontSize: font.size.lg,
                                                  ":hover": {
                                                    color: "#e04132",
                                                  },
                                                }}
                                              />
                                            </IconButton>
                                          </div>
                                        </div>
                                      </section>
                                    )}
                                  </Dropzone>
                                )}
                              />
                            )}
                          </>
                        ),
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={
                        typeof watchFields?.documents?.seller?.qt
                          ?.sellerQtAdditionalFile !== "object"
                          ? watchFields?.documents?.seller?.qt
                              ?.sellerQtAdditionalFile
                          : watchFields?.documents?.seller?.qt
                              ?.sellerQtAdditionalFile?.name
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </>
    );
  };

  const FreeShipping = (index = 0) => {
    return (
      <>
        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>หมายเหตุ</SpanLabel>
          <TextField
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={shippingDetail?.fulfillmentProcess[index]?.remark}
            onChange={({ target }) =>
              setShippingDetail((prevState) => {
                const updateShipping = [...prevState.fulfillmentProcess];
                updateShipping[index] = {
                  ...updateShipping[index],
                  ...{ remark: target.value },
                };
                return {
                  ...prevState,
                  fulfillmentProcess: updateShipping,
                };
              })
            }
          />
        </Grid>

        {!isEdit && (
          <>
            <Line />

            <Grid container xs={12} mb={2}>
              <SpanTitle>เอกสาร</SpanTitle>
            </Grid>

            <Grid container xs={12}>
              <Grid item xs={12}>
                <SpanLabel isMargin>
                  {`ใบเสนอราคา (ของร้านค้า)`} <span>*</span>
                </SpanLabel>
                <FormControl fullWidth>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <>
                          {checkNotEmpty(
                            watchFields?.documents?.seller?.qt?.sellerQtFile,
                          ) ? (
                            <IconButton
                              onClick={() =>
                                setValue("documents.seller.qt.sellerQtFile", "")
                              }
                            >
                              <DeleteOutlineIcon
                                sx={{
                                  color: Primary,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          ) : (
                            <Controller
                              name="documents.seller.qt.sellerQtFile"
                              control={control}
                              render={({ field: { onChange } }) => (
                                <Dropzone
                                  onDrop={(acceptedFiles) =>
                                    onChange(acceptedFiles[0])
                                  }
                                  accept={[".pdf"]}
                                >
                                  {({ getRootProps, getInputProps }) => (
                                    <section>
                                      <div {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        <div>
                                          <IconButton>
                                            <AttachFileIcon
                                              sx={{
                                                color: Black,
                                                cursor: "pointer",
                                                fontSize: font.size.lg,
                                                ":hover": {
                                                  color: "#e04132",
                                                },
                                              }}
                                            />
                                          </IconButton>
                                        </div>
                                      </div>
                                    </section>
                                  )}
                                </Dropzone>
                              )}
                            />
                          )}
                        </>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                    value={
                      typeof watchFields?.documents?.seller?.qt
                        ?.sellerQtFile !== "object"
                        ? watchFields?.documents?.seller?.qt?.sellerQtFile
                        : watchFields?.documents?.seller?.qt?.sellerQtFile?.name
                    }
                  />
                  <FormHelperText
                    error={!!errors?.documents?.seller?.qt?.sellerQtFile}
                  >
                    {errors?.documents?.seller?.qt?.sellerQtFile?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container xs={12} mt={2}>
              <Grid item xs={12}>
                <SpanLabel isMargin>{`เอกสารเพิ่มเติม`}</SpanLabel>
                <FormControl fullWidth>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <>
                          {checkNotEmpty(
                            watchFields?.documents?.seller?.qt
                              ?.sellerQtAdditionalFile,
                          ) ? (
                            <IconButton
                              onClick={() =>
                                setValue(
                                  "documents.seller.qt.sellerQtAdditionalFile",
                                  "",
                                )
                              }
                            >
                              <DeleteOutlineIcon
                                sx={{
                                  color: Primary,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          ) : (
                            <Controller
                              name="documents.seller.qt.sellerQtAdditionalFile"
                              control={control}
                              render={({ field: { onChange } }) => (
                                <Dropzone
                                  onDrop={(acceptedFiles) =>
                                    onChange(acceptedFiles[0])
                                  }
                                  accept={[".pdf"]}
                                >
                                  {({ getRootProps, getInputProps }) => (
                                    <section>
                                      <div {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        <div>
                                          <IconButton>
                                            <AttachFileIcon
                                              sx={{
                                                color: Black,
                                                cursor: "pointer",
                                                fontSize: font.size.lg,
                                                ":hover": {
                                                  color: "#e04132",
                                                },
                                              }}
                                            />
                                          </IconButton>
                                        </div>
                                      </div>
                                    </section>
                                  )}
                                </Dropzone>
                              )}
                            />
                          )}
                        </>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                    value={
                      typeof watchFields?.documents?.seller?.qt
                        ?.sellerQtAdditionalFile !== "object"
                        ? watchFields?.documents?.seller?.qt
                            ?.sellerQtAdditionalFile
                        : watchFields?.documents?.seller?.qt
                            ?.sellerQtAdditionalFile?.name
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}
      </>
    );
  };

  const CashOnDelivery = (index = 0) => {
    return (
      <>
        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>
            {`ค่าจัดส่งโดยประมาณ (บาท)`} <span>*</span>
          </SpanLabel>
          <FormControl fullWidth>
            <Controller
              name={`fulfillmentProcess[${index}].shippingFee`}
              control={control}
              render={({ field }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  variant="outlined"
                  thousandSeparator=","
                  decimalSeparator="."
                  allowNegative={false}
                  decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
                  sx={{
                    width: "100%",
                    "& input": {
                      textAlign: "right",
                    },
                  }}
                  error={!!errors?.fulfillmentProcess?.[index]?.shippingFee}
                  helperText={
                    errors?.fulfillmentProcess?.[index]?.shippingFee?.message
                  }
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} mt={2}>
          <SpanLabel isMargin>หมายเหตุ</SpanLabel>
          <TextField
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={shippingDetail?.fulfillmentProcess[index]?.remark}
            onChange={({ target }) =>
              setShippingDetail((prevState) => {
                const updateShipping = [...prevState.fulfillmentProcess];
                updateShipping[index] = {
                  ...updateShipping[index],
                  ...{ remark: target.value },
                };
                return {
                  ...prevState,
                  fulfillmentProcess: updateShipping,
                };
              })
            }
          />
        </Grid>

        {watchFields?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง" ||
        watchFields?.fulfillmentProcess?.[1]?.type === "มีค่าจัดส่ง" ? (
          <Grid
            container
            xs={12}
            mt={3}
            mb={0}
            justifyContent={"space-between"}
          >
            <Grid item display="flex" alignItems="center">
              <SpanTitle>ค่าจัดส่งรวมทั้งหมด</SpanTitle>
            </Grid>

            <Grid item display="flex" justifyContent="center">
              <SumPrice>
                {watchFields?.typeFirst === "มีค่าจัดส่ง"
                  ? watchFields?.typeSecond
                    ? shippingDetail?.fulfillmentProcess[1]?.type ===
                      "มีค่าจัดส่ง"
                      ? convertIntToCurrency(
                          currencyToNumber(watchFields?.shippingFeeFirst) +
                            currencyToNumber(
                              shippingDetail?.fulfillmentProcess[1].shippingFee,
                            ),
                        )
                      : convertIntToCurrency(+watchFields?.shippingFeeFirst)
                    : convertIntToCurrency(+watchFields?.shippingFeeFirst)
                  : shippingDetail?.fulfillmentProcess.length > 1
                    ? shippingDetail?.fulfillmentProcess[1]?.type ===
                      "มีค่าจัดส่ง"
                      ? convertIntToCurrency(
                          +shippingDetail?.fulfillmentProcess[1].shippingFee,
                        )
                      : 0
                    : 0}
              </SumPrice>
              <SpanTitle slim>บาท</SpanTitle>
            </Grid>
          </Grid>
        ) : null}

        {!isEdit && (
          <>
            <Line />

            <Grid container xs={12} mb={2}>
              <SpanTitle>เอกสาร</SpanTitle>
            </Grid>

            <Grid container xs={12}>
              <Grid item xs={12}>
                <SpanLabel isMargin>
                  {`ใบเสนอราคา (ของร้านค้า)`} <span>*</span>
                </SpanLabel>
                <FormControl fullWidth>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <>
                          {checkNotEmpty(
                            watchFields?.documents?.seller?.qt?.sellerQtFile,
                          ) ? (
                            <IconButton
                              onClick={() =>
                                setValue("documents.seller.qt.sellerQtFile", "")
                              }
                            >
                              <DeleteOutlineIcon
                                sx={{
                                  color: Primary,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          ) : (
                            <Controller
                              name="documents.seller.qt.sellerQtFile"
                              control={control}
                              render={({ field: { onChange } }) => (
                                <Dropzone
                                  onDrop={(acceptedFiles) =>
                                    onChange(acceptedFiles[0])
                                  }
                                  accept={[".pdf"]}
                                >
                                  {({ getRootProps, getInputProps }) => (
                                    <section>
                                      <div {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        <div>
                                          <IconButton>
                                            <AttachFileIcon
                                              sx={{
                                                color: Black,
                                                cursor: "pointer",
                                                fontSize: font.size.lg,
                                                ":hover": {
                                                  color: "#e04132",
                                                },
                                              }}
                                            />
                                          </IconButton>
                                        </div>
                                      </div>
                                    </section>
                                  )}
                                </Dropzone>
                              )}
                            />
                          )}
                        </>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                    value={
                      typeof watchFields?.documents?.seller?.qt
                        ?.sellerQtFile !== "object"
                        ? watchFields?.documents?.seller?.qt?.sellerQtFile
                        : watchFields?.documents?.seller?.qt?.sellerQtFile?.name
                    }
                  />
                  <FormHelperText
                    error={!!errors?.documents?.seller?.qt?.sellerQtFile}
                  >
                    {errors?.documents?.seller?.qt?.sellerQtFile?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container xs={12} mt={2}>
              <Grid item xs={12}>
                <SpanLabel isMargin>{`เอกสารเพิ่มเติม`}</SpanLabel>
                <FormControl fullWidth>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <>
                          {checkNotEmpty(watchFields?.additionalFile) ? (
                            <IconButton
                              onClick={() => setValue("additionalFile", "")}
                            >
                              <DeleteOutlineIcon
                                sx={{
                                  color: Primary,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          ) : (
                            <Controller
                              name="additionalFile"
                              control={control}
                              render={({ field: { onChange } }) => (
                                <Dropzone
                                  onDrop={(acceptedFiles) =>
                                    onChange(acceptedFiles[0])
                                  }
                                  accept={[".pdf"]}
                                >
                                  {({ getRootProps, getInputProps }) => (
                                    <section>
                                      <div {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        <div>
                                          <IconButton>
                                            <AttachFileIcon
                                              sx={{
                                                color: Black,
                                                cursor: "pointer",
                                                fontSize: font.size.lg,
                                                ":hover": {
                                                  color: "#e04132",
                                                },
                                              }}
                                            />
                                          </IconButton>
                                        </div>
                                      </div>
                                    </section>
                                  )}
                                </Dropzone>
                              )}
                            />
                          )}
                        </>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                    value={
                      typeof watchFields?.additionalFile !== "object"
                        ? watchFields?.additionalFile
                        : watchFields?.additionalFile?.name
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}
      </>
    );
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        {isEdit
          ? "แก้ไขรายละเอียดการจัดส่งสินค้า"
          : "รายละเอียดการจัดส่งสินค้า"}
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit, checkErrors)}>
        <DialogContent>
          <FormControl>
            <RadioGroup
              row
              name="row-radio-buttons-group"
              value={isProductIsReady}
              defaultValue={isProductIsReady}
              onChange={({ target }) => {
                reset();
                setIsProductIsReady(target.value);
              }}
            >
              <FormControlLabel
                value="ready"
                control={
                  <Radio
                    sx={{
                      color: "#D8D8DC",
                      padding: "5px",
                      "&.Mui-checked": {
                        color: "#E13B30",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 25,
                      },
                    }}
                  />
                }
                label="มีสินค้าพร้อมจัดส่ง"
              />
              <FormControlLabel
                value="not_ready"
                control={
                  <Radio
                    sx={{
                      color: "#D8D8DC",
                      padding: "5px",
                      "&.Mui-checked": {
                        color: "#E13B30",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 25,
                      },
                    }}
                  />
                }
                label="สินค้าไม่พร้อมจัดส่ง"
              />
            </RadioGroup>
          </FormControl>

          {isProductIsReady === "ready" ? (
            <>
              <Line />

              <Grid container xs={12} mb={2}>
                <SpanTitle>การจัดส่งสินค้า</SpanTitle>
              </Grid>

              <Grid container xs={12}>
                <Grid item xs={12}>
                  <SpanLabel isMargin>
                    รูปแบบการจัดส่ง <span>*</span>
                  </SpanLabel>
                  <FormControl fullWidth>
                    <Controller
                      name="method"
                      control={control}
                      render={({ field }) => (
                        <StyledSelect
                          {...field}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 4,
                                ".MuiList-root": {
                                  padding: 0,
                                },
                                ".MuiMenuItem-root": {
                                  minHeight: {
                                    sm: 48,
                                    xs: 40,
                                  },
                                  px: {
                                    sm: 2,
                                    xs: 1.5,
                                  },
                                  py: {
                                    sm: 1.5,
                                    xs: 1,
                                  },
                                  fontSize: {
                                    sm: font.size.md,
                                    xs: font.size.sm,
                                  },
                                },
                              },
                            },
                          }}
                          onChange={({ target }) => {
                            setValue("method", target.value);
                            setValue("fulfillmentProcess[1].carrier", "อื่นๆ");
                            setValue(
                              "fulfillmentProcess[1].type",
                              "มีค่าจัดส่ง",
                            );
                          }}
                          error={!!errors.method}
                          helperText={errors.method?.message}
                        >
                          {deliveryMethods.map((option) => (
                            <MenuItem value={option}>{option}</MenuItem>
                          ))}
                        </StyledSelect>
                      )}
                    />
                    <FormHelperText error={!!errors.method}>
                      {errors.method?.message}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                {watchFields?.method === "ส่งไปถึงลูกค้าโดยตรง" && (
                  <>
                    <Grid container xs={12} mt={2}>
                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess[0]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 8
                        }
                      >
                        <SpanLabel isMargin>
                          ขนส่งที่ใช้ในการจัดส่ง <span>*</span>
                        </SpanLabel>
                      </Grid>

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess[0]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 0
                        }
                      ></Grid>

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess[0]?.carrier ===
                          "อื่นๆ"
                            ? 3.6
                            : 4
                        }
                      >
                        <SpanLabel isMargin>
                          ค่าจัดส่ง <span>*</span>
                        </SpanLabel>
                      </Grid>
                    </Grid>

                    <Grid container xs={12}>
                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess[0]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 8
                        }
                        pr={2}
                      >
                        <FormControl fullWidth>
                          <Controller
                            name="fulfillmentProcess[0].carrier"
                            control={control}
                            render={({ field }) => (
                              <StyledSelect
                                {...field}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      borderRadius: 4,
                                      ".MuiList-root": {
                                        padding: 0,
                                      },
                                      ".MuiMenuItem-root": {
                                        minHeight: {
                                          sm: 48,
                                          xs: 40,
                                        },
                                        px: {
                                          sm: 2,
                                          xs: 1.5,
                                        },
                                        py: {
                                          sm: 1.5,
                                          xs: 1,
                                        },
                                        fontSize: {
                                          sm: font.size.md,
                                          xs: font.size.sm,
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                {carriers.map((option, index) => (
                                  <MenuItem value={option} key={index}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            )}
                          />
                          <FormHelperText
                            error={!!errors.fulfillmentProcess?.[0]?.carrier}
                          >
                            {errors.fulfillmentProcess?.[0]?.carrier?.message}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      {watchFields?.fulfillmentProcess?.[0]?.carrier ===
                        "อื่นๆ" && (
                        <Grid item xs={4.2} pr={2}>
                          <Grid item xs={100}>
                            <FormControl>
                              <Controller
                                name="fulfillmentProcess[0].carrierAdditional"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    error={
                                      !!errors.fulfillmentProcess?.[0]
                                        ?.carrierAdditional
                                    }
                                    helperText={
                                      errors.fulfillmentProcess?.[0]
                                        ?.carrierAdditional?.message
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      )}

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[0]?.carrier ===
                          "อื่นๆ"
                            ? 3.6
                            : 4
                        }
                      >
                        <FormControl fullWidth>
                          <Controller
                            name="fulfillmentProcess[0].type"
                            control={control}
                            render={({ field }) => (
                              <StyledSelect
                                {...field}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      borderRadius: 4,
                                      ".MuiList-root": {
                                        padding: 0,
                                      },
                                      ".MuiMenuItem-root": {
                                        minHeight: {
                                          sm: 48,
                                          xs: 40,
                                        },
                                        px: {
                                          sm: 2,
                                          xs: 1.5,
                                        },
                                        py: {
                                          sm: 1.5,
                                          xs: 1,
                                        },
                                        fontSize: {
                                          sm: font.size.md,
                                          xs: font.size.sm,
                                        },
                                      },
                                    },
                                  },
                                }}
                                error={!!errors?.fulfillmentProcess?.[0]?.type}
                                helperText={
                                  errors?.fulfillmentProcess?.[0]?.type?.message
                                }
                                onChange={({ target }) => {
                                  setValue(
                                    "fulfillmentProcess[0].type",
                                    target.value,
                                  );
                                  setValue(
                                    "fulfillmentProcess[0].shippingFee",
                                    0,
                                  );
                                }}
                              >
                                {deliveryFee.map((option, index) => (
                                  <MenuItem value={option} key={index}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            )}
                          />
                          <FormHelperText
                            error={!!errors.fulfillmentProcess?.[0]?.type}
                          >
                            {errors.fulfillmentProcess?.[0]?.type?.message}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {watchFields?.fulfillmentProcess?.[0]?.type ===
                      "มีค่าจัดส่ง" && ShippingFeeApplies()}

                    {watchFields?.fulfillmentProcess?.[0]?.type ===
                      "ไม่มีค่าจัดส่ง" && FreeShipping()}

                    {watchFields?.fulfillmentProcess?.[0]?.type ===
                      "เก็บเงินปลายทาง" && CashOnDelivery(0)}
                  </>
                )}

                {watchFields?.method === "ส่งไปที่ขนส่ง" && (
                  <>
                    <Grid item xs={12} mt={2}>
                      <SpanLabel
                        highlight
                      >{`การส่งของที่ 1 (ร้านค้าส่งไปขนส่ง)`}</SpanLabel>
                    </Grid>

                    <Grid container xs={12} mt={2}>
                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[0]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 8
                        }
                      >
                        <SpanLabel isMargin>
                          ขนส่งที่ใช้ในการจัดส่ง <span>*</span>
                        </SpanLabel>
                      </Grid>

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[0]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 0
                        }
                      ></Grid>

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[0]?.carrier ===
                          "อื่นๆ"
                            ? 3.6
                            : 4
                        }
                      >
                        <SpanLabel isMargin>
                          ค่าจัดส่ง <span>*</span>
                        </SpanLabel>
                      </Grid>
                    </Grid>

                    <Grid container xs={12}>
                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[0]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 8
                        }
                        pr={2}
                      >
                        <FormControl fullWidth>
                          <Controller
                            name="fulfillmentProcess[0].carrier"
                            control={control}
                            render={({ field }) => (
                              <StyledSelect
                                {...field}
                                error={
                                  !!errors?.fulfillmentProcess?.[0]?.carrier
                                }
                                helperText={
                                  errors?.fulfillmentProcess?.[0]?.carrier
                                    ?.message
                                }
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      borderRadius: 4,
                                      ".MuiList-root": {
                                        padding: 0,
                                      },
                                      ".MuiMenuItem-root": {
                                        minHeight: {
                                          sm: 48,
                                          xs: 40,
                                        },
                                        px: {
                                          sm: 2,
                                          xs: 1.5,
                                        },
                                        py: {
                                          sm: 1.5,
                                          xs: 1,
                                        },
                                        fontSize: {
                                          sm: font.size.md,
                                          xs: font.size.sm,
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                {carriers.map((option, index) => (
                                  <MenuItem value={option} key={index}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            )}
                          />
                          <FormHelperText
                            error={!!errors?.fulfillmentProcess?.[0]?.carrier}
                          >
                            {errors?.fulfillmentProcess?.[0]?.carrier?.message}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      {watchFields?.fulfillmentProcess?.[0]?.carrier ===
                        "อื่นๆ" && (
                        <Grid item xs={4.2} pr={2}>
                          <Grid item xs={100}>
                            <FormControl>
                              <Controller
                                name="fulfillmentProcess[0].carrierAdditional"
                                control={control}
                                render={({ filed }) => (
                                  <TextField
                                    {...filed}
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    error={
                                      !!errors.fulfillmentProcess?.[0]
                                        ?.carrierAdditional
                                    }
                                    helperText={
                                      errors.fulfillmentProcess?.[0]
                                        ?.carrierAdditional?.message
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      )}

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[0]?.carrier ===
                          "อื่นๆ"
                            ? 3.6
                            : 4
                        }
                      >
                        <FormControl fullWidth>
                          <Controller
                            name="fulfillmentProcess[0].type"
                            control={control}
                            render={({ field }) => (
                              <StyledSelect
                                {...field}
                                error={!!errors?.fulfillmentProcess?.[0]?.type}
                                helperText={
                                  errors?.fulfillmentProcess?.[0]?.type?.message
                                }
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      borderRadius: 4,
                                      ".MuiList-root": {
                                        padding: 0,
                                      },
                                      ".MuiMenuItem-root": {
                                        minHeight: {
                                          sm: 48,
                                          xs: 40,
                                        },
                                        px: {
                                          sm: 2,
                                          xs: 1.5,
                                        },
                                        py: {
                                          sm: 1.5,
                                          xs: 1,
                                        },
                                        fontSize: {
                                          sm: font.size.md,
                                          xs: font.size.sm,
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                {multipleDeliveryFee.map((option) => (
                                  <MenuItem value={option}>{option}</MenuItem>
                                ))}
                              </StyledSelect>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>

                    {watchFields?.fulfillmentProcess?.[0]?.type ===
                      "มีค่าจัดส่ง" && (
                      <Grid item xs={12} mt={2}>
                        <SpanLabel isMargin>
                          {`ค่าจัดส่ง (บาท)`} <span>*</span>
                        </SpanLabel>
                        <FormControl fullWidth>
                          <Controller
                            name="fulfillmentProcess[0].shippingFee"
                            control={control}
                            render={({ field }) => (
                              <NumericFormat
                                {...field}
                                customInput={TextField}
                                variant="outlined"
                                thousandSeparator=","
                                decimalSeparator="."
                                allowNegative={false}
                                decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
                                sx={{
                                  width: "100%",
                                  "& input": {
                                    textAlign: "right",
                                  },
                                }}
                                error={
                                  !!errors?.fulfillmentProcess?.[0]?.shippingFee
                                }
                                helperText={
                                  errors?.fulfillmentProcess?.[0]?.shippingFee
                                    ?.message
                                }
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                    )}

                    <Grid item xs={12} mt={2}>
                      <SpanLabel
                        highlight
                      >{`การส่งของที่ 2 (ขนส่งส่งไปลูกค้า)`}</SpanLabel>
                    </Grid>

                    <Grid container xs={12} mt={2}>
                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[1]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 8
                        }
                      >
                        <SpanLabel isMargin>
                          ขนส่งที่ใช้ในการจัดส่ง <span>*</span>
                        </SpanLabel>
                      </Grid>

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[1]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 0
                        }
                      ></Grid>

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[1]?.carrier ===
                          "อื่นๆ"
                            ? 3.6
                            : 4
                        }
                      >
                        <SpanLabel isMargin>
                          ค่าจัดส่ง <span>*</span>
                        </SpanLabel>
                      </Grid>
                    </Grid>

                    <Grid container xs={12}>
                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[1]?.carrier ===
                          "อื่นๆ"
                            ? 4.2
                            : 8
                        }
                        pr={2}
                      >
                        <FormControl fullWidth>
                          <Controller
                            name="fulfillmentProcess[1].carrier"
                            control={control}
                            render={({ field }) => (
                              <StyledSelect
                                {...field}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      borderRadius: 4,
                                      ".MuiList-root": {
                                        padding: 0,
                                      },
                                      ".MuiMenuItem-root": {
                                        minHeight: {
                                          sm: 48,
                                          xs: 40,
                                        },
                                        px: {
                                          sm: 2,
                                          xs: 1.5,
                                        },
                                        py: {
                                          sm: 1.5,
                                          xs: 1,
                                        },
                                        fontSize: {
                                          sm: font.size.md,
                                          xs: font.size.sm,
                                        },
                                      },
                                    },
                                  },
                                }}
                                error={
                                  !!errors?.fulfillmentProcess?.[1]?.carrier
                                }
                                helperText={
                                  errors?.fulfillmentProcess?.[1]?.carrier
                                    ?.message
                                }
                              >
                                <MenuItem value="อื่นๆ">{`อื่นๆ`}</MenuItem>
                              </StyledSelect>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      {watchFields?.fulfillmentProcess?.[1]?.carrier ===
                        "อื่นๆ" && (
                        <Grid item xs={4.2} pr={2}>
                          <Grid item xs={100}>
                            <FormControl>
                              <Controller
                                name="fulfillmentProcess[1].carrierAdditional"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    error={
                                      !!errors?.fulfillmentProcess?.[1]
                                        ?.carrierAdditional
                                    }
                                    helperText={
                                      errors?.fulfillmentProcess?.[1]
                                        ?.carrierAdditional?.message
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      )}

                      <Grid
                        item
                        xs={
                          watchFields?.fulfillmentProcess?.[1]?.carrier ===
                          "อื่นๆ"
                            ? 3.6
                            : 4
                        }
                      >
                        <FormControl fullWidth>
                          <Controller
                            name="fulfillmentProcess[1].type"
                            control={control}
                            render={({ field }) => (
                              <StyledSelect
                                {...field}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      borderRadius: 4,
                                      ".MuiList-root": {
                                        padding: 0,
                                      },
                                      ".MuiMenuItem-root": {
                                        minHeight: {
                                          sm: 48,
                                          xs: 40,
                                        },
                                        px: {
                                          sm: 2,
                                          xs: 1.5,
                                        },
                                        py: {
                                          sm: 1.5,
                                          xs: 1,
                                        },
                                        fontSize: {
                                          sm: font.size.md,
                                          xs: font.size.sm,
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                {["มีค่าจัดส่ง", "เก็บเงินปลายทาง"].map(
                                  (option) => (
                                    <MenuItem value={option}>{option}</MenuItem>
                                  ),
                                )}
                              </StyledSelect>
                            )}
                          />
                          <FormHelperText
                            error={!!errors?.fulfillmentProcess?.[1]?.type}
                          >
                            {errors?.fulfillmentProcess?.[1]?.type?.message}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {watchFields?.fulfillmentProcess?.[0]?.type ===
                    "มีค่าจัดส่ง" ? (
                      <>
                        {watchFields?.fulfillmentProcess?.[1]?.type ===
                        "มีค่าจัดส่ง" ? (
                          <Grid item xs={12} mt={2}>
                            <SpanLabel isMargin>
                              {`ค่าจัดส่ง (บาท)`} <span>*</span>
                            </SpanLabel>
                            <FormControl fullWidth>
                              <Controller
                                name="fulfillmentProcess[1].shippingFee"
                                control={control}
                                render={({ field }) => (
                                  <NumericFormat
                                    {...field}
                                    customInput={TextField}
                                    variant="outlined"
                                    thousandSeparator=","
                                    decimalSeparator="."
                                    allowNegative={false}
                                    decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
                                    sx={{
                                      width: "100%",
                                      "& input": {
                                        textAlign: "right",
                                      },
                                    }}
                                    error={
                                      !!errors?.fulfillmentProcess?.[1]
                                        ?.shippingFee
                                    }
                                    helperText={
                                      errors?.fulfillmentProcess?.[1]
                                        ?.shippingFee?.message
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        ) : (
                          <Grid item xs={12} mt={2}>
                            <SpanLabel isMargin>
                              {`ค่าจัดส่งโดยประมาณ (บาท)`} <span>*</span>
                            </SpanLabel>
                            <FormControl fullWidth>
                              <Controller
                                name="fulfillmentProcess[1].shippingFee"
                                control={control}
                                render={({ field }) => (
                                  <NumericFormat
                                    {...field}
                                    customInput={TextField}
                                    variant="outlined"
                                    thousandSeparator=","
                                    decimalSeparator="."
                                    allowNegative={false}
                                    decimalScale={2} // กำหนดให้เป็นทศนิยม 2 ตำแหน่ง
                                    sx={{
                                      width: "100%",
                                      "& input": {
                                        textAlign: "right",
                                      },
                                    }}
                                    error={
                                      !!errors?.fulfillmentProcess?.[1]
                                        ?.shippingFee
                                    }
                                    helperText={
                                      errors?.fulfillmentProcess?.[1]
                                        ?.shippingFee?.message
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        )}

                        <Grid item xs={12} mt={2}>
                          <SpanLabel isMargin>หมายเหตุ</SpanLabel>
                          <FormControl fullWidth>
                            <Controller
                              name="fulfillmentProcess[0].remark"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  variant="outlined"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>

                        <Grid
                          container
                          xs={12}
                          mt={3}
                          mb={0}
                          justifyContent={"space-between"}
                        >
                          <Grid item display="flex" alignItems="center">
                            <SpanTitle>ค่าจัดส่งรวมทั้งหมด</SpanTitle>
                          </Grid>
                          <Grid item display="flex" justifyContent="center">
                            <SumPrice>
                              {watchFields?.fulfillmentProcess?.[0]?.type ===
                              "มีค่าจัดส่ง"
                                ? watchFields?.method === "ส่งไปที่ขนส่ง"
                                  ? watchFields?.fulfillmentProcess?.[1]
                                      ?.type === "มีค่าจัดส่ง"
                                    ? convertIntToCurrency(
                                        currencyToNumber(
                                          watchFields?.fulfillmentProcess?.[0]
                                            ?.shippingFee,
                                        ) +
                                          currencyToNumber(
                                            watchFields?.fulfillmentProcess?.[1]
                                              ?.shippingFee,
                                          ),
                                      )
                                    : convertIntToCurrency(
                                        watchFields?.fulfillmentProcess?.[0]
                                          ?.shippingFee,
                                      )
                                  : convertIntToCurrency(
                                      watchFields?.fulfillmentProcess?.[0]
                                        ?.shippingFee,
                                    )
                                : convertIntToCurrency(
                                    watchFields?.fulfillmentProcess?.[1]
                                      ?.shippingFee ?? 0,
                                  )}
                            </SumPrice>
                            <SpanTitle slim>บาท</SpanTitle>
                          </Grid>
                        </Grid>

                        <Line />

                        <Grid container xs={12} justifyContent="space-between">
                          <Grid container xs={12} mb={2}>
                            <SpanTitle>ช่องทางการชำระเงิน</SpanTitle>
                          </Grid>
                          <Grid item xs={5.8}>
                            <SpanLabel isMargin>
                              ชื่อบัญชี <span>*</span>
                            </SpanLabel>
                            <FormControl fullWidth>
                              <Controller
                                name="payment.info.bankAccountName"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    error={
                                      !!errors?.payment?.info?.bankAccountName
                                    }
                                    helperText={
                                      errors?.payment?.info?.bankAccountName
                                        ?.message
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          <Grid item xs={5.8}>
                            <SpanLabel isMargin>
                              เลขบัญชี <span>*</span>
                            </SpanLabel>
                            <FormControl fullWidth>
                              <Controller
                                name="payment.info.bankNo"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors?.payment?.info?.bankNo}
                                    helperText={
                                      errors?.payment?.info?.bankNo?.message
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          {!isEdit && (
                            <>
                              <Line />

                              <Grid container xs={12} mb={2}>
                                <SpanTitle>เอกสาร</SpanTitle>
                              </Grid>

                              <Grid container xs={12}>
                                <Grid item xs={12}>
                                  <SpanLabel isMargin>
                                    {`ใบเสนอราคา (ของร้านค้า)`} <span>*</span>
                                  </SpanLabel>
                                  <FormControl fullWidth>
                                    <StyledTextField
                                      variant="outlined"
                                      fullWidth
                                      InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                          <>
                                            {checkNotEmpty(
                                              watchFields?.documents?.seller?.qt
                                                ?.sellerQtFile,
                                            ) ? (
                                              <IconButton
                                                onClick={() =>
                                                  setValue(
                                                    "documents.seller.qt.sellerQtFile",
                                                    "",
                                                  )
                                                }
                                              >
                                                <DeleteOutlineIcon
                                                  sx={{
                                                    color: Primary,
                                                    cursor: "pointer",
                                                  }}
                                                />
                                              </IconButton>
                                            ) : (
                                              <Controller
                                                name="documents.seller.qt.sellerQtFile"
                                                control={control}
                                                render={({
                                                  field: { onChange },
                                                }) => (
                                                  <Dropzone
                                                    onDrop={(acceptedFiles) =>
                                                      onChange(acceptedFiles[0])
                                                    }
                                                    accept={[".pdf"]}
                                                  >
                                                    {({
                                                      getRootProps,
                                                      getInputProps,
                                                    }) => (
                                                      <section>
                                                        <div
                                                          {...getRootProps()}
                                                        >
                                                          <input
                                                            {...getInputProps()}
                                                          />
                                                          <div>
                                                            <IconButton>
                                                              <AttachFileIcon
                                                                sx={{
                                                                  color: Black,
                                                                  cursor:
                                                                    "pointer",
                                                                  fontSize:
                                                                    font.size
                                                                      .lg,
                                                                  ":hover": {
                                                                    color:
                                                                      "#e04132",
                                                                  },
                                                                }}
                                                              />
                                                            </IconButton>
                                                          </div>
                                                        </div>
                                                      </section>
                                                    )}
                                                  </Dropzone>
                                                )}
                                              />
                                            )}
                                          </>
                                        ),
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                      value={
                                        typeof watchFields?.documents?.seller
                                          ?.qt?.sellerQtFile !== "object"
                                          ? watchFields?.documents?.seller?.qt
                                              ?.sellerQtFile
                                          : watchFields?.documents?.seller?.qt
                                              ?.sellerQtFile?.name
                                      }
                                    />
                                    <FormHelperText
                                      error={
                                        !!errors.documents?.seller?.qt
                                          ?.sellerQtFile
                                      }
                                    >
                                      {
                                        errors.documents?.seller?.qt
                                          ?.sellerQtFile?.message
                                      }
                                    </FormHelperText>
                                  </FormControl>
                                </Grid>
                              </Grid>

                              <Grid container xs={12} mt={2}>
                                <Grid item xs={12}>
                                  <SpanLabel
                                    isMargin
                                  >{`เอกสารเพิ่มเติม`}</SpanLabel>
                                  <FormControl fullWidth>
                                    <StyledTextField
                                      variant="outlined"
                                      fullWidth
                                      InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                          <>
                                            {checkNotEmpty(
                                              watchFields?.additionalFile,
                                            ) ? (
                                              <IconButton
                                                onClick={() =>
                                                  setValue("additionalFile", "")
                                                }
                                              >
                                                <DeleteOutlineIcon
                                                  sx={{
                                                    color: Primary,
                                                    cursor: "pointer",
                                                  }}
                                                />
                                              </IconButton>
                                            ) : (
                                              <Controller
                                                name="additionalFile"
                                                control={control}
                                                render={({
                                                  field: { onChange },
                                                }) => (
                                                  <Dropzone
                                                    onDrop={(acceptedFiles) =>
                                                      onChange(acceptedFiles[0])
                                                    }
                                                    accept={[".pdf"]}
                                                  >
                                                    {({
                                                      getRootProps,
                                                      getInputProps,
                                                    }) => (
                                                      <section>
                                                        <div
                                                          {...getRootProps()}
                                                        >
                                                          <input
                                                            {...getInputProps()}
                                                          />
                                                          <div>
                                                            <IconButton>
                                                              <AttachFileIcon
                                                                sx={{
                                                                  color: Black,
                                                                  cursor:
                                                                    "pointer",
                                                                  fontSize:
                                                                    font.size
                                                                      .lg,
                                                                  ":hover": {
                                                                    color:
                                                                      "#e04132",
                                                                  },
                                                                }}
                                                              />
                                                            </IconButton>
                                                          </div>
                                                        </div>
                                                      </section>
                                                    )}
                                                  </Dropzone>
                                                )}
                                              />
                                            )}
                                          </>
                                        ),
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                      value={
                                        typeof watchFields?.additionalFile !==
                                        "object"
                                          ? watchFields?.additionalFile
                                          : watchFields?.additionalFile?.name
                                      }
                                    />
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </>
                    ) : null}

                    {watchFields?.fulfillmentProcess?.[0]?.type ===
                      "ไม่มีค่าจัดส่ง" &&
                      watchFields?.fulfillmentProcess?.[1]?.type ===
                        "เก็บเงินปลายทาง" &&
                      CashOnDelivery(1)}
                  </>
                )}

                {watchFields?.method === "ลูกค้ารับเอง" && (
                  <>
                    <Grid container xs={12} mt={2}>
                      <SpanLabel isMargin>
                        ปักหมุด Location <span>*</span>
                      </SpanLabel>
                      <WrapperMapsContainer>
                        <MapsContainer>
                          <CustomMap
                            markerLocation={{
                              lat: shippingDetail?.fulfillmentProcess[0]
                                ?.pickupAddress?.coordinates?.lat,
                              lng: shippingDetail?.fulfillmentProcess[0]
                                ?.pickupAddress?.coordinates?.lng,
                            }}
                            changeMarkerLocation={(value) => {
                              setValue("address");
                              setShippingDetail((prevState) => {
                                const updateShipping = [
                                  ...prevState.fulfillmentProcess,
                                ];
                                updateShipping[0] = {
                                  ...updateShipping[0],
                                  ...{
                                    pickupAddress: {
                                      ...updateShipping[0].pickupAddress,
                                      coordinates: value,
                                    },
                                  },
                                };
                                return {
                                  ...prevState,
                                  fulfillmentProcess: updateShipping,
                                };
                              });
                            }}
                            changeSeletedAddress={(value) => {
                              setShippingDetail((prevState) => {
                                const updateShipping = [
                                  ...prevState.fulfillmentProcess,
                                ];
                                updateShipping[0] = {
                                  ...updateShipping[0],
                                  ...{
                                    pickupAddress: {
                                      ...updateShipping[0].pickupAddress,
                                      address: value,
                                    },
                                  },
                                };
                                return {
                                  ...prevState,
                                  fulfillmentProcess: updateShipping,
                                };
                              });
                            }}
                            inputPlaceholder="ตำแหน่งที่ตั้งร้านค้า"
                          />
                        </MapsContainer>
                      </WrapperMapsContainer>
                    </Grid>

                    <Grid item xs={12} mt={2}>
                      <SpanLabel isMargin>
                        ที่อยู่ร้านค้า <span>*</span>
                      </SpanLabel>
                      <FormControl fullWidth>
                        <Controller
                          name="address"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              variant="outlined"
                              fullWidth
                              error={!!errors.address}
                              helperText={errors.address?.message}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} mt={2}>
                      <SpanLabel isMargin>
                        เบอร์ติดต่อ <span>*</span>
                      </SpanLabel>
                      <FormControl fullWidth>
                        <Controller
                          name="tel"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              variant="outlined"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              error={!!errors.tel}
                              helperText={errors.tel?.message}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} mt={2}>
                      <SpanLabel isMargin>หมายเหตุ</SpanLabel>
                      <FormControl fullWidth>
                        <Controller
                          name="remark"
                          control={control}
                          render={({ filed }) => (
                            <TextField
                              {...filed}
                              variant="outlined"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    {!isEdit && (
                      <>
                        <Line />

                        <Grid container xs={12} mb={2}>
                          <SpanTitle>เอกสาร</SpanTitle>
                        </Grid>

                        <Grid container xs={12}>
                          <Grid item xs={12}>
                            <SpanLabel isMargin>
                              {`ใบเสนอราคา (ของร้านค้า)`} <span>*</span>
                            </SpanLabel>
                            <FormControl fullWidth>
                              <StyledTextField
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <>
                                      {checkNotEmpty(watchFields?.qtFile) ? (
                                        <IconButton
                                          onClick={() => setValue("qtFile", "")}
                                        >
                                          <DeleteOutlineIcon
                                            sx={{
                                              color: Primary,
                                              cursor: "pointer",
                                            }}
                                          />
                                        </IconButton>
                                      ) : (
                                        <Controller
                                          name="qtFile"
                                          control={control}
                                          render={({ field: { onChange } }) => (
                                            <Dropzone
                                              onDrop={(acceptedFiles) =>
                                                onChange(acceptedFiles[0])
                                              }
                                              accept={[".pdf"]}
                                            >
                                              {({
                                                getRootProps,
                                                getInputProps,
                                              }) => (
                                                <section>
                                                  <div {...getRootProps()}>
                                                    <input
                                                      {...getInputProps()}
                                                    />
                                                    <div>
                                                      <IconButton>
                                                        <AttachFileIcon
                                                          sx={{
                                                            color: Black,
                                                            cursor: "pointer",
                                                            fontSize:
                                                              font.size.lg,
                                                            ":hover": {
                                                              color: "#e04132",
                                                            },
                                                          }}
                                                        />
                                                      </IconButton>
                                                    </div>
                                                  </div>
                                                </section>
                                              )}
                                            </Dropzone>
                                          )}
                                        />
                                      )}
                                    </>
                                  ),
                                }}
                                InputLabelProps={{ shrink: true }}
                                value={
                                  typeof watchFields?.qtFile !== "object"
                                    ? watchFields?.qtFile
                                    : watchFields?.qtFile?.name
                                }
                              />
                              <FormHelperText error={!!errors.qtFile}>
                                {errors.qtFile?.message}
                              </FormHelperText>
                            </FormControl>
                          </Grid>
                        </Grid>

                        <Grid container xs={12} mt={2}>
                          <Grid item xs={12}>
                            <SpanLabel isMargin>{`เอกสารเพิ่มเติม`}</SpanLabel>
                            <FormControl fullWidth>
                              <StyledTextField
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <>
                                      {checkNotEmpty(
                                        watchFields?.additionalFile,
                                      ) ? (
                                        <IconButton
                                          onClick={() =>
                                            setValue("additionalFile", "")
                                          }
                                        >
                                          <DeleteOutlineIcon
                                            sx={{
                                              color: Primary,
                                              cursor: "pointer",
                                            }}
                                          />
                                        </IconButton>
                                      ) : (
                                        <Controller
                                          name="additionalFile"
                                          control={control}
                                          render={({ field: { onChange } }) => (
                                            <Dropzone
                                              onDrop={(acceptedFiles) =>
                                                onChange(acceptedFiles[0])
                                              }
                                              accept={[".pdf"]}
                                            >
                                              {({
                                                getRootProps,
                                                getInputProps,
                                              }) => (
                                                <section>
                                                  <div {...getRootProps()}>
                                                    <input
                                                      {...getInputProps()}
                                                    />
                                                    <div>
                                                      <IconButton>
                                                        <AttachFileIcon
                                                          sx={{
                                                            color: Black,
                                                            cursor: "pointer",
                                                            fontSize:
                                                              font.size.lg,
                                                            ":hover": {
                                                              color: "#e04132",
                                                            },
                                                          }}
                                                        />
                                                      </IconButton>
                                                    </div>
                                                  </div>
                                                </section>
                                              )}
                                            </Dropzone>
                                          )}
                                        />
                                      )}
                                    </>
                                  ),
                                }}
                                InputLabelProps={{ shrink: true }}
                                value={
                                  typeof watchFields?.additionalFile !==
                                  "object"
                                    ? watchFields?.additionalFile
                                    : watchFields?.additionalFile?.name
                                }
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </>
          ) : (
            <Grid xs={12} mt={3}>
              <SpanLabel>
                สาเหตุที่ไม่พร้อมส่ง <span>*</span>
              </SpanLabel>
              <Controller
                name="failureReason"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4.2}
                    error={!!errors.failureReason}
                    helperText={errors.failureReason?.message}
                  />
                )}
              />
            </Grid>
          )}

          {isAdmin &&
            isEdit &&
            (shippingDetail?.status?.value === "preparing" ||
              shippingDetail?.status?.value === "delivering") && (
              <>
                <Line />
                <Grid item xs={12} mt={2}>
                  <WrapperBox>
                    <SpanTitle slim unMargin>
                      วันที่ชำระเงิน
                    </SpanTitle>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <StyledDatePicker
                        inputFormat="DD-MM-YYYY"
                        value={shippingDetail?.payment?.paymentDate}
                        onChange={(value) => {
                          const newValue = shippingDetail;
                          newValue.payment.paymentDate = value.$d;
                          setShippingDetail({ ...newValue });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            sx={{
                              // my: 2,
                              maxWidth: 200,
                            }}
                            // error={!(updateOrder?.slipDate !== "")}
                            // helperText={
                            //   !!formErrors[`promo_start_time`]
                            //     ? formErrors[`promo_start_time`].message
                            //     : ""
                            // }
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </WrapperBox>
                </Grid>

                <Grid container xs={12} mt={3} mb={5}>
                  <UploadImage
                    callBackFile={(fileCallBlack) => {
                      if (fileCallBlack !== "") {
                        const newValue = shippingDetail;
                        newValue.payment.paymentProof = fileCallBlack;
                        setShippingDetail({ ...newValue });
                      }
                    }}
                    closeAlert={false}
                  >
                    {checkNotEmpty(shippingDetail?.payment?.paymentProof) ? (
                      <WrapperUploadImage>
                        <WrapperImgSlip>
                          <ImgSlip
                            src={shippingDetail?.payment?.paymentProof.docUrl}
                            alt="paySlipImage"
                          />
                        </WrapperImgSlip>
                      </WrapperUploadImage>
                    ) : (
                      <WrapperUploadImage>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<AddIcon />}
                        >
                          แนบหลักฐานการชำระเงิน
                        </Button>
                      </WrapperUploadImage>
                    )}
                  </UploadImage>

                  {checkNotEmpty(shippingDetail?.payment?.paymentProof) && (
                    <Grid container xs={12} justifyContent="center" mt={2}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<SaveAltIcon />}
                        onClick={() => {
                          const fileUrl =
                            shippingDetail?.payment?.paymentProof.docUrl;
                          const link = document.createElement("a");
                          link.href = fileUrl;
                          link.setAttribute("target", "_blank"); // เปิดในแท็บใหม่
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        ดาวน์โหลด
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </>
            )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={() => onClose()}
            fullWidth
            sx={{ maxWidth: 180 }}
          >
            ยกเลิก
          </Button>

          <Button
            variant="contained"
            size="large"
            // onClick={() => onSubmit()}
            fullWidth
            sx={{ maxWidth: 180 }}
            type="submit"
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default ConfirmDialog;
